import { BOARD_PATTERNS, normalizePatternIndex } from './boardPatterns';

/** All board coordinates and face hit boxes use the same 750rpx design space. */
export const BOARD_WIDTH = 720;
export const BOARD_HEIGHT = 812;
export const CARD_SIZE = 92;
export const CARD_STEP = 94;
export type BoardMode = 'daily' | 'world';

export interface BoardPosition {
  x: number;
  y: number;
  zIndex: number;
  layer: number;
  zone: 'main' | 'auxiliary' | 'reserve-left' | 'reserve-right';
  size?: number;
  sourceId?: string;
}

type CoverPosition = Pick<BoardPosition, 'x' | 'y' | 'zIndex'> & Partial<Pick<BoardPosition, 'zone' | 'size' | 'sourceId' | 'layer'>>;

/** Auxiliary columns are independent piles, including legacy diagonally offset saves. */
export const coversPosition = (lower: CoverPosition, upper: CoverPosition): boolean => {
  const dx = Math.abs(lower.x - upper.x);
  const above = lower.sourceId && upper.sourceId ? (upper.layer ?? 0) > (lower.layer ?? 0) : upper.zIndex > lower.zIndex;
  const lowerSize = lower.size ?? CARD_SIZE, upperSize = upper.size ?? CARD_SIZE;
  // Exact source boundaries must not become sliver overlaps after uniform scaling.
  if (!above || upper.x >= lower.x + lowerSize - 1e-6 || lower.x >= upper.x + upperSize - 1e-6 ||
    upper.y >= lower.y + lowerSize - 1e-6 || lower.y >= upper.y + upperSize - 1e-6) return false;
  // Old auxiliary layers shifted sideways by 8rpx, crossing the next column's
  // rim by 4–12rpx. That decorative overlap must not lock its exposed top card.
  if (lower.zone === 'auxiliary' && upper.zone === 'auxiliary' && dx >= CARD_STEP / 2) return false;
  return true;
};

/** Same-layer faces never overlap; half-card offsets only occur between layers. */
export const generateBoardLayout = (level: number, total: number, layers: number): BoardPosition[] => {
  const positions: BoardPosition[] = [];
  if (level === 1) {
    for (let layer = 0; layer < 2; layer++) {
      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 3; col++) {
          positions.push({
            x: 194 + col * CARD_STEP + layer * CARD_STEP / 2,
            y: 140 + row * CARD_STEP + layer * CARD_STEP / 2,
            zIndex: positions.length,
            layer,
            zone: 'main'
          });
        }
      }
    }
    return positions;
  }

  const reserveCount = Math.min(level * 12, total - 18);
  const mainCount = total - reserveCount;
  const cells = Array.from({ length: 42 }, (_, index) => ({ col: index % 7, row: Math.floor(index / 7) }))
    .filter(({ col, row }) => !((row === 0 || row === 5) && (col === 0 || col === 6)))
    .sort((a, b) => (Math.abs(a.col - 3) * 1.15 + Math.abs(a.row - 2.5))
      - (Math.abs(b.col - 3) * 1.15 + Math.abs(b.row - 2.5)));
  const weights = Array.from({ length: layers }, (_, layer) => layers + 2 - layer);
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  const counts = weights.map(weight => Math.min(cells.length, Math.floor(mainCount * weight / weightSum)));
  let unassigned = mainCount - counts.reduce((sum, count) => sum + count, 0);
  for (let cursor = 0; unassigned > 0; cursor++) {
    const index = cursor % layers;
    if (counts[index] < cells.length) { counts[index]++; unassigned--; }
  }

  for (let layer = 0; layer < layers; layer++) {
    const selected = cells.slice(0, counts[layer]).sort((a, b) => a.row - b.row || a.col - b.col);
    for (const { col, row } of selected) {
      positions.push({
        x: 26 + col * CARD_STEP + (layer % 2 ? 24 : -24),
        y: 32 + row * CARD_STEP + (layer % 2 ? 48 : 0),
        zIndex: positions.length,
        layer,
        zone: 'main'
      });
    }
  }

  const perSide = reserveCount / 2;
  const fanStep = Math.min(12, 152 / Math.max(1, perSide - 1));
  for (let side = 0; side < 2; side++) {
    const startX = side === 0 ? 26 : BOARD_WIDTH - 26 - CARD_SIZE - (perSide - 1) * fanStep;
    for (let index = 0; index < perSide; index++) {
      positions.push({
        x: startX + index * fanStep,
        y: 696,
        zIndex: positions.length,
        layer: layers + index,
        zone: side === 0 ? 'reserve-left' : 'reserve-right'
      });
    }
  }
  return positions;
};

export const fitBoardToWindow = (windowWidth = 375, windowHeight = 650): number => {
  const rpx = windowWidth / 750;
  const availableHeight = windowHeight / rpx - 480;
  return Math.min(1, 710 / BOARD_WIDTH, Math.max(0.5, availableHeight / BOARD_HEIGHT));
};

/** Translate and uniformly scale the full source board; never thin, rotate or append piles. */
export const generateModeLayout = (_mode: BoardMode, pattern: number): BoardPosition[] => {
  const design = BOARD_PATTERNS[normalizePatternIndex(pattern)], nodes = design.nodes;
  const minX = Math.min(...nodes.map(n => n[0])), minY = Math.min(...nodes.map(n => n[1]));
  const spanX = Math.max(...nodes.map(n => n[0])) + 8 - minX;
  const spanY = Math.max(...nodes.map(n => n[1])) + 8 - minY;
  const unit = Math.min(CARD_SIZE / 8, (BOARD_WIDTH - 24) / spanX, (BOARD_HEIGHT - 24) / spanY);
  const left = (BOARD_WIDTH - spanX * unit) / 2, top = (BOARD_HEIGHT - spanY * unit) / 2;
  return nodes.map(([x, y, layer, mold], index) => ({
    x: left + (x - minX) * unit, y: top + (y - minY) * unit, size: 8 * unit,
    layer, zIndex: index, sourceId: layer + '-' + x + '-' + y,
    zone: mold === 2 ? (x < minX + spanX / 2 ? 'reserve-left' : 'reserve-right') : 'main'
  }));
};
